import React, { ReactNode, useRef, useEffect } from 'react'
import classnames from 'classnames/bind'
import type {
  WorkflowResource,
  ShallowTaskWorkflowMilestone,
} from '@adc/sdk/types'
import { useTransition, animated, SpringConfig } from 'react-spring'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../utils/symbols'
import { closing } from '@adc/blueprint'
import WorkflowDetails from './WorkflowDetails'
export type {
  ExtendedShallowTaskWorkflowGroup,
  ExtendedShallowTaskWorkflowMilestone,
} from './WorkflowDetails'
import type { ExtendedShallowTaskWorkflowGroup } from './WorkflowDetails'

/**
 * Extended WorkflowResource type for consumers that use pipeline-mode features
 * (animated groups, pulse, sub-progress, detail variants, etc.).
 * Structurally compatible with WorkflowResource — passes `extends WorkflowResource` checks.
 */
export interface ExtendedWorkflowResource {
  __typename: 'AuctionTransaction' | 'Listing' | 'ReoOffer' | 'TrusteeContract'
  workflow?: {
    current_status_label: string
    task_groups: ExtendedShallowTaskWorkflowGroup[]
  } | null
}
// @ts-ignore
import styles from './styles.scss'
import { Alert } from '../elementals'

const cx = classnames.bind(styles)

interface FragmentMeta {
  [GRAPH_API_FRAGMENT_DEFINITION]: string
  [GRAPH_API_FRAGMENT_REFERENCE]: string
}

export interface CustomTaskActionArgs<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  isDisabled?: boolean
  compact?: boolean
}

export interface CustomIconArgs<T extends WorkflowResource = WorkflowResource> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  default: ReactNode
  compact?: boolean
}

export interface InlinePromptArgs<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
}

export interface MilestoneRightArgs<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  /** Pre-rendered default pieces — compose, reorder, wrap, or replace */
  defaults: {
    date: ReactNode
    action: ReactNode
    taskActions: ReactNode
  }
  compact?: boolean
}

export interface MilestoneContentArgs<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  /** Pre-rendered default pieces — compose, reorder, wrap, or replace */
  defaults: {
    label: ReactNode
    subProgress: ReactNode
    detail: ReactNode
    duration: ReactNode
    processing: ReactNode
  }
  compact?: boolean
}

/**
 * Animation style for phase card entrance.
 * - 'none': no animation (default, backward compatible)
 * - 'fade-up': slide up 20px + fade in
 * - 'fade-up-scale': slide up 16px + scale from 0.97 + fade in (spring physics)
 * - 'accordion': expand height from 0 + fade in
 * - 'spring-bounce': slide up with spring overshoot + fade in
 */
export type PhaseEntryAnimation =
  | 'none'
  | 'fade-up'
  | 'fade-up-scale'
  | 'accordion'
  | 'spring-bounce'

interface AnimationPreset {
  from: Record<string, unknown>
  enter: Record<string, unknown>
  config: SpringConfig
}

const ANIMATION_PRESETS: Record<
  Exclude<PhaseEntryAnimation, 'none'>,
  AnimationPreset
> = {
  'fade-up': {
    from: { opacity: 0, transform: 'translateY(20px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    config: { duration: 1000 },
  },
  'fade-up-scale': {
    from: { opacity: 0, transform: 'translateY(16px) scale(0.97)' },
    enter: { opacity: 1, transform: 'translateY(0px) scale(1)' },
    config: { tension: 90, friction: 28 },
  },
  accordion: {
    from: { opacity: 0, maxHeight: 0, overflow: 'hidden' as unknown },
    enter: { opacity: 1, maxHeight: 800, overflow: 'visible' as unknown },
    config: { tension: 80, friction: 30 },
  },
  'spring-bounce': {
    from: { opacity: 0, transform: 'translateY(24px)' },
    enter: { opacity: 1, transform: 'translateY(0px)' },
    config: { tension: 70, friction: 18 },
  },
}

export interface InstructionActionArgs<
  T extends WorkflowResource = WorkflowResource
> {
  task: ShallowTaskWorkflowMilestone
  resource: T
  action: string
}

export interface ShallowTaskWorkflowProps<
  T extends WorkflowResource = WorkflowResource
> {
  resource: T
  slots?: Partial<{
    taskActions: ((args: CustomTaskActionArgs<T>) => ReactNode) | null
    taskIcon: ((args: CustomIconArgs<T>) => ReactNode) | null
    inlinePrompt: ((args: InlinePromptArgs<T>) => ReactNode) | null
    /** Full control over milestone right section (date + actions). Receives default pieces. */
    milestoneRight: ((args: MilestoneRightArgs<T>) => ReactNode) | null
    /** Full control over milestone center content (label + detail + progress). Receives default pieces. */
    milestoneContent: ((args: MilestoneContentArgs<T>) => ReactNode) | null
    /** Called when an instructions banner action button is clicked. Consumer handles domain-specific UI (e.g. modals). */
    instructionAction: ((args: InstructionActionArgs<T>) => ReactNode) | null
  }>
  isDisabled?: boolean
  errorMessage?: string
  compact?: boolean
  featureFlags?: Record<string, boolean>
  /**
   * Animation style for new phase cards entering the DOM.
   * Default: 'none' (no animation, backward compatible).
   */
  phaseEntryAnimation?: PhaseEntryAnimation
  /**
   * When true, auto-scrolls to keep the latest active phase visible.
   * Default: false.
   */
  autoScroll?: boolean
}

interface ShallowTaskWorkflowComponent {
  <T extends WorkflowResource = WorkflowResource>(
    props: ShallowTaskWorkflowProps<T>
  ): JSX.Element
  WorkflowResourceBaseFragment: FragmentMeta
  ReoOfferWorkflowFragment: FragmentMeta
}

const ShallowTaskWorkflow: ShallowTaskWorkflowComponent = <
  T extends WorkflowResource = WorkflowResource
>({
  resource,
  slots,
  isDisabled,
  errorMessage,
  compact,
  featureFlags = {},
  phaseEntryAnimation = 'none',
  autoScroll = false,
}: ShallowTaskWorkflowProps<T>) => {
  const groups = resource.workflow?.task_groups || []
  const workflowStatus = resource.workflow?.current_status_label ?? 'Upcoming'
  const containerRef = useRef<HTMLDivElement>(null)
  const prevGroupCountRef = useRef(groups.length)

  // Auto-scroll when new groups are added
  useEffect(() => {
    if (!autoScroll) return
    if (groups.length > prevGroupCountRef.current && containerRef.current) {
      const lastChild = containerRef.current.lastElementChild
      if (lastChild) {
        lastChild.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }
    prevGroupCountRef.current = groups.length
  }, [groups.length, autoScroll])

  const isAnimated = phaseEntryAnimation !== 'none'
  const preset = isAnimated ? ANIMATION_PRESETS[phaseEntryAnimation] : undefined

  const groupTransitions = useTransition(groups, {
    keys: (group: (typeof groups)[number]) => group.code,
    from: preset?.from ?? { opacity: 1 },
    enter: preset?.enter ?? { opacity: 1 },
    config: preset?.config ?? { duration: 0 },
    trail: isAnimated ? 50 : 0,
  })

  return (
    <>
      {errorMessage && (
        <Alert
          className={cx('error-alert')}
          role='error'
          elmId='workflow-error'
          content={errorMessage}
        />
      )}
      <div
        ref={containerRef}
        className={cx('milestones-container', {
          disabled: isDisabled,
          compact,
        })}
      >
        {isAnimated
          ? groupTransitions((style, group) => (
              <animated.div key={group.code} style={style}>
                <WorkflowDetails
                  group={group}
                  workflowStatus={workflowStatus}
                  resource={resource}
                  slots={slots}
                  isDisabled={isDisabled}
                  compact={compact}
                  featureFlags={featureFlags}
                />
              </animated.div>
            ))
          : groups.map(group => (
              <WorkflowDetails
                key={group.code}
                group={group}
                workflowStatus={workflowStatus}
                resource={resource}
                slots={slots}
                isDisabled={isDisabled}
                compact={compact}
                featureFlags={featureFlags}
              />
            ))}
      </div>
    </>
  )
}

// Fragments sourced from @adc/blueprint (centralized)
const { workflowResourceBase, workflow } = closing.fragments

// `workflow` is a builder (viewingAs is opt-in). The re-exposed static fragment
// omits viewingAs; consumers that need it call
// `closing.fragments.workflow({ withViewingAs: true })` directly.
const reoOfferWorkflow = workflow()

// Base fragment for general WorkflowResource
ShallowTaskWorkflow.WorkflowResourceBaseFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: workflowResourceBase.definition,
  [GRAPH_API_FRAGMENT_REFERENCE]: workflowResourceBase.reference,
}

// ReoOffer specific fragment
ShallowTaskWorkflow.ReoOfferWorkflowFragment = {
  [GRAPH_API_FRAGMENT_DEFINITION]: reoOfferWorkflow.definition,
  [GRAPH_API_FRAGMENT_REFERENCE]: reoOfferWorkflow.reference,
}

export default ShallowTaskWorkflow
